import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ThumbsUp, Send, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_COLORS = {
  general: 'text-[#e8e8e8] bg-[#a0a0ab]/10',
  feature: 'text-[#ff6b6b] bg-[#ff6b6b]/10',
  content: 'text-[#20c997] bg-[#20c997]/10',
  bug: 'text-[#ffc107] bg-[#ffc107]/10',
};

export default function Community() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      const { data } = await axios.get(`${API}/suggestions`, { withCredentials: true });
      setSuggestions(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/suggestions`, { content, category }, { withCredentials: true });
      setSuggestions(prev => [data, ...prev]);
      setContent('');
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (id) => {
    try {
      const { data } = await axios.post(`${API}/suggestions/${id}/vote`, {}, { withCredentials: true });
      setSuggestions(prev =>
        prev.map(s => s.id === id ? { ...s, votes: data.votes } : s)
      );
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 pt-6 pb-4 space-y-6">
        {/* Header */}
        <div className="animate-fade-up">
          <h1 className="font-heading text-2xl font-bold text-white">{t('community.title')}</h1>
          <p className="text-[#e8e8e8] text-sm mt-1">{t('community.subtitle')}</p>
        </div>

        {/* Submit form */}
        <div className="kurso-card p-5 animate-fade-up" style={{ animationDelay: '0.1s' }}>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(t('community.categories', { returnObjects: true })).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(key)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                    category === key
                      ? `${CATEGORY_COLORS[key]} border-current`
                      : 'border-white/10 text-[#e8e8e8] hover:border-white/20'
                  }`}
                  data-testid={`category-${key}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="relative">
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={t('community.placeholder')}
                maxLength={500}
                rows={3}
                className="w-full bg-[#0f0e17] border border-white/10 text-white placeholder-[#a0a0ab] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#20c997]/50 transition-colors resize-none"
                data-testid="suggestion-textarea"
              />
              <span className="absolute bottom-3 right-3 text-xs text-[#e8e8e8]">{content.length}/500</span>
            </div>

            <button
              type="submit"
              disabled={submitting || !content.trim()}
              className="btn-primary w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-suggestion-btn"
            >
              {submitting ? (
                <div className="w-4 h-4 rounded-full border-2 border-[#0f0e17] border-t-transparent animate-spin" />
              ) : (
                <>
                  <Send size={15} />
                  {t('community.submit')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Suggestions list */}
        <div className="space-y-3 animate-fade-up" style={{ animationDelay: '0.2s' }}>
          {loading ? (
            [1, 2, 3].map(i => <div key={i} className="animate-shimmer h-20 rounded-2xl" />)
          ) : suggestions.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare size={32} className="text-[#e8e8e8] mx-auto mb-3" />
              <p className="text-[#e8e8e8] text-sm">{t('community.noSuggestions')}</p>
            </div>
          ) : (
            suggestions.map((s, i) => (
              <div key={s.id} className="kurso-card p-4 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => handleVote(s.id)}
                    className="flex flex-col items-center gap-0.5 shrink-0 pt-0.5 hover:scale-110 transition-transform"
                    data-testid={`vote-${s.id}`}
                  >
                    <ThumbsUp size={16} className="text-[#e8e8e8] hover:text-[#ffc107] transition-colors" />
                    <span className="text-xs text-[#e8e8e8] font-semibold">{s.votes}</span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white text-sm font-medium leading-snug">{s.content}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#e8e8e8]">par {s.author_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[s.category] || CATEGORY_COLORS.general}`}>
                        {s.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}
